
import { ReactNode, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import CriticalHomepageCSS from "../performance/CriticalHomepageCSS";

interface LayoutProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  path?: string;
  author?: string;
  children?: ReactNode;
}

const Layout = ({
  title = "Freelance With Andy",
  description = "The ultimate resource for freelancers and product managers, providing expert insights, actionable strategies, and hands-on guidance to help you succeed.",
  keywords = "freelance, web development, projects, product, andy, andrew, courses, digital",
  image = "/lovable-uploads/ba4f0776-af2f-4f44-a112-8b601bcf43b8.png",
  type = "website",
  path = "",
  author = "Andrew Nady",
  children
}: LayoutProps) => {
  const siteUrl = "https://freelancewithandy.com";
  const canonicalUrl = path ? `${siteUrl}${path.startsWith('/') ? path : `/${path}`}` : siteUrl;
  const fullImageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;
  const isHomepage = path === "";

  // Defer main CSS loading after critical content renders
  useEffect(() => {
    if (isHomepage) {
      const deferMainCSS = () => {
        // Wait for critical content to render before loading full CSS
        setTimeout(() => {
          const links = document.querySelectorAll('link[rel="stylesheet"][media="print"]');
          links.forEach((link: any) => {
            link.media = 'all';
          });
        }, 100);
      };
      
      if (document.readyState === 'complete') {
        deferMainCSS();
      } else {
        window.addEventListener('load', deferMainCSS);
        return () => window.removeEventListener('load', deferMainCSS);
      }
    }
  }, [isHomepage]);

  return (
    <>
      {isHomepage && <CriticalHomepageCSS />}
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="author" content={author} />
        
        {/* Google Site Verification */}
        <meta name="google-site-verification" content="PUiqk5nL-fu_MPxHXiT5emSkxxJnT3EZRE5LxvJJxRg" />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:type" content={type} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={fullImageUrl} />
        <meta property="og:site_name" content="Freelance With Andy" />
        
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={fullImageUrl} />
        
        {/* Favicon */}
        <link rel="icon" href="/lovable-uploads/1c8aa900-f626-467e-a48f-8076054d376c.png" />
        
        {/* Preload LCP image for homepage - Critical for performance */}
        {path === "" && (
          <>
            <link 
              rel="preload" 
              as="image" 
              href="/lovable-uploads/ba4f0776-af2f-4f44-a112-8b601bcf43b8.png"
              fetchPriority="high"
              crossOrigin="anonymous"
            />
            <link rel="dns-prefetch" href="https://freelance-with-andy.lovable.app" />
          </>
        )}
        
        {/* Optimized Font Loading - Non-blocking */}
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function() {
                var fontLink = document.createElement('link');
                fontLink.rel = 'stylesheet';
                fontLink.href = 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap';
                fontLink.media = 'print';
                fontLink.onload = function() { this.media = 'all'; };
                document.head.appendChild(fontLink);
                
                // Fallback for browsers without JavaScript
                var noscriptTag = document.querySelector('noscript[data-font-fallback]');
                if (!noscriptTag) {
                  noscriptTag = document.createElement('noscript');
                  noscriptTag.setAttribute('data-font-fallback', 'true');
                  noscriptTag.innerHTML = '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap">';
                  document.head.appendChild(noscriptTag);
                }
              })();
            `
          }}
        />
        
        {/* Preload critical font files to eliminate render-blocking */}
        <link 
          rel="preload" 
          as="font" 
          type="font/woff2" 
          href="https://fonts.gstatic.com/s/lato/v24/S6uyw4BMUTPHjx4wXg.woff2"
          crossOrigin="anonymous"
        />
        <link 
          rel="preload" 
          as="font" 
          type="font/woff2" 
          href="https://fonts.gstatic.com/s/lato/v24/S6u9w4BMUTPHh6UVSwiPGQ.woff2"
          crossOrigin="anonymous"
        />
        
        {/* Performance Optimizations */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="preconnect" href="https://fmuvniyvftmenrgeywtv.supabase.co" />
        <link rel="dns-prefetch" href="https://fmuvniyvftmenrgeywtv.supabase.co" />
        <link rel="preconnect" href="https://connect.facebook.net" />
        
        {/* Page Transition Hints */}
        <link rel="prefetch" href="/" />
        <link rel="prefetch" href="/about" />
        <link rel="prefetch" href="/projects" />
        <link rel="prefetch" href="/courses" />
        <link rel="prefetch" href="/blog" />
        <link rel="prefetch" href="/contact" />
      </Helmet>
      {children}
    </>
  );
};

export default Layout;
